import { Badge, Flex, Space, Text } from "@mantine/core";
import React, { type JSX } from "react";

import type { ChallengeInfo } from "@/sales-tools/challenges/api/getChallengesForEmployee";

import { ChallengeLimit } from "@/sales-tools/challenges/component/ChallengeLimit";
import { ChallengeTarget } from "@/sales-tools/challenges/component/ChallengeTarget";
import { sortTeams } from "@/sales-tools/challenges/utils/findMyTeam";
import { isDisqualified } from "@/sales-tools/challenges/utils/isDisqualified";
import { useLogger } from "@/sdk";
import { userCache } from "@/shared-utils";

export function ChallengeProgression({
    challenge,
    displayDirection,
}: {
    challenge: ChallengeInfo;
    displayDirection: "horizontal" | "vertical";
}): JSX.Element | null {
    const logger = useLogger({ module: "ChallengeProgression", supportTeam: "sales" });
    sortTeams(logger, challenge.details, userCache.employeeId!);

    return (
        <>
            <Flex
                w="100%"
                direction={displayDirection === "vertical" ? "column" : "row"}
                justify={displayDirection === "vertical" ? "auto" : "space-between"}
            >
                {challenge.details.map((d, index) => {
                    const targets = d.conditions.filter((c) => c.threshold === "TARGET");
                    const limits = d.conditions.filter((c) => c.threshold !== "TARGET");

                    const isDQed = isDisqualified(d.conditions);
                    return (
                        <Flex
                            w={challenge.details.length === 1 ? "100%" : "auto"}
                            direction="column"
                            key={d.empIds.toString()}
                        >
                            {index > 0 || displayDirection === "horizontal" ? <Space key={index} h="sm" /> : undefined}
                            {d.teamName ? (
                                <Text
                                    data-testid={`team-name-${index}`}
                                    key={`${d.teamName}-${index}`}
                                    size="xs"
                                    fw={700}
                                    c={isDQed ? "dark.3" : "dark.0"}
                                    mb="3xs"
                                >
                                    {d.teamName}
                                    {d.wasSuccessful ? " 👑" : ""}
                                </Text>
                            ) : undefined}
                            {isDQed ? (
                                <Badge tt="none" c="dark.2" color="dark.4" radius="xs" key={`${d.teamName}-badge`}>
                                    <Text size="xxs">Disqualified—too many cancels</Text>
                                </Badge>
                            ) : (
                                <>
                                    {targets?.map((t) => {
                                        return <ChallengeTarget key={t.goalType} target={t} />;
                                    })}
                                    {limits?.map((l) => {
                                        return <ChallengeLimit key={l.goalType} limit={l} />;
                                    })}
                                </>
                            )}
                        </Flex>
                    );
                })}
            </Flex>
        </>
    );
}
